import type { Conversation } from '../Conversation';

import { ExclusiveQueue } from './ExclusiveQueue';
import type { SubagentType } from './subagentTypes';

export const DEFAULT_MAX_CONCURRENT_SUBAGENTS = 3;
export const DEFAULT_MAX_SUBAGENTS_PER_MESSAGE = 10;

export enum SubagentStatus {
    /** Started past the concurrency limit, waiting for a running subagent to finish. */
    QUEUED = 'queued',
    RUNNING = 'running',
    COMPLETED = 'completed',
    FAILED = 'failed',
    KILLED = 'killed',
}

export type SubagentOutcome = SubagentStatus.COMPLETED | SubagentStatus.FAILED;

export interface SubagentRun {
    readonly id: string;
    readonly description: string;
    readonly type: SubagentType;
    readonly conversation: Conversation;
    readonly status: SubagentStatus;
    readonly killRequested: boolean;
    readonly queuedAt: number;
    /** When it left the queue and began running; absent while it waits. */
    readonly startedAt?: number;
    readonly endedAt?: number;
}

interface Waiter {
    readonly id: string;
    readonly admit: (admitted: boolean) => void;
}

/**
 * Every subagent a root agent has started this session, and the limits on how many run at once and
 * how many one message may start. Past the first limit a subagent waits its turn instead of being
 * refused, so a batch always finishes.
 *
 * Owned by the root agent rather than the spawning tool, since tool instances are rebuilt
 * whenever the agent's context loads. Runs are replaced rather than mutated, so `getRuns()`
 * returns a stable reference until something changes and can back `useSyncExternalStore`.
 */
export class SubagentRegistry {
    private runs: readonly SubagentRun[] = [];
    private readonly listeners = new Set<VoidFunction>();
    /**
     * Checks and captures render the one live video, and the text-contrast check briefly
     * recolours text to measure it, so two running at once would each see the other's
     * mid-probe state.
     */
    public readonly captureQueue = new ExclusiveQueue();
    /**
     * The chat shows one question at a time and dismisses any that arrive while one is showing,
     * so questions from subagents running together wait their turn.
     */
    public readonly questionQueue = new ExclusiveQueue();

    private waiting: readonly Waiter[] = [];

    constructor(
        private readonly maxConcurrent: number = DEFAULT_MAX_CONCURRENT_SUBAGENTS,
        private readonly maxPerMessage: number = DEFAULT_MAX_SUBAGENTS_PER_MESSAGE,
    ) {}

    public getMaxConcurrent(): number {
        return this.maxConcurrent;
    }

    public getMaxPerMessage(): number {
        return this.maxPerMessage;
    }

    public getRuns(): readonly SubagentRun[] {
        return this.runs;
    }

    public getRun(id: string): SubagentRun | undefined {
        return this.runs.find((run) => run.id === id);
    }

    public getRunningCount(): number {
        return this.runs.filter((run) => run.status === SubagentStatus.RUNNING).length;
    }

    public getQueuedCount(): number {
        return this.runs.filter((run) => run.status === SubagentStatus.QUEUED).length;
    }

    /**
     * Records a subagent and gives it a slot, running it now if one is free and queueing it
     * otherwise. `admitted` resolves true once it may run, or false if it is killed or `signal`
     * aborts while it waits. Checking and claiming a slot happen in one synchronous step, so calls
     * running in parallel can't both claim the last one.
     */
    public admit(
        {
            description,
            type,
            conversation,
        }: Pick<SubagentRun, 'description' | 'type' | 'conversation'>,
        signal?: AbortSignal,
    ): { run: SubagentRun; admitted: Promise<boolean> } {
        const id = conversation.getId();
        const now = Date.now();
        const runsNow = this.getRunningCount() < this.maxConcurrent;
        const run: SubagentRun = {
            id,
            description,
            type,
            conversation,
            status: runsNow ? SubagentStatus.RUNNING : SubagentStatus.QUEUED,
            killRequested: false,
            queuedAt: now,
            ...(runsNow && { startedAt: now }),
        };
        this.runs = [...this.runs, run];
        this.notify();
        if (runsNow) {
            return { run, admitted: Promise.resolve(true) };
        }

        const admitted = new Promise<boolean>((resolve) => {
            const leaveQueue = () => this.leaveQueue(id, false);
            this.waiting = [
                ...this.waiting,
                {
                    id,
                    admit: (isAdmitted) => {
                        signal?.removeEventListener('abort', leaveQueue);
                        resolve(isAdmitted);
                    },
                },
            ];
            signal?.addEventListener('abort', leaveQueue, { once: true });
        });
        return { run, admitted };
    }

    /**
     * A killed run keeps its slot until it has actually wound down, so the limit counts
     * every subagent still holding a request open. Its slot then goes to the next in line.
     */
    public finish(id: string, outcome: SubagentOutcome): SubagentRun | undefined {
        this.leaveQueue(id, false);
        const finished = this.update(id, (run) => ({
            ...run,
            status: run.killRequested ? SubagentStatus.KILLED : outcome,
            endedAt: Date.now(),
        }));
        this.admitNext();
        return finished;
    }

    public kill(id: string): void {
        const run = this.getRun(id);
        const isActive =
            run?.status === SubagentStatus.RUNNING || run?.status === SubagentStatus.QUEUED;
        if (!run || !isActive || run.killRequested) {
            return;
        }
        this.update(id, (current) => ({ ...current, killRequested: true }));
        if (run.status === SubagentStatus.QUEUED) {
            this.leaveQueue(id, false);
            return;
        }
        run.conversation.cancel();
    }

    private leaveQueue(id: string, admitted: boolean): void {
        const waiter = this.waiting.find((candidate) => candidate.id === id);
        if (!waiter) {
            return;
        }
        this.waiting = this.waiting.filter((candidate) => candidate !== waiter);
        waiter.admit(admitted);
    }

    private admitNext(): void {
        while (this.getRunningCount() < this.maxConcurrent && this.waiting.length > 0) {
            const [next] = this.waiting;
            this.update(next.id, (run) => ({
                ...run,
                status: SubagentStatus.RUNNING,
                startedAt: Date.now(),
            }));
            this.leaveQueue(next.id, true);
        }
    }

    public subscribe(listener: VoidFunction): VoidFunction {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    private update(id: string, change: (run: SubagentRun) => SubagentRun): SubagentRun | undefined {
        let updated: SubagentRun | undefined;
        this.runs = this.runs.map((run) => {
            if (run.id !== id) {
                return run;
            }
            updated = change(run);
            return updated;
        });
        if (updated) {
            this.notify();
        }
        return updated;
    }

    private notify(): void {
        this.listeners.forEach((listener) => listener());
    }
}
